/**
 * Quiet hours — THE rule (MAR-159 / MAR-163). Dependency-free: it requires nothing, so
 * `schemas.js` can refine with it and the web app can bundle it for the settings cards.
 *
 * Everything that decides whether an hour is quiet, which window a channel obeys, or whether a
 * window can ever open again reads this module:
 *
 *  - the enforcer (`governance-enforcer.checkQuietHours`, email at send time and decision time),
 *  - the SMS gate (`sms-compliance/checks.checkCanSendSms`, proactive texts AND replies),
 *  - `sms-reply-handler`'s pre-generation check,
 *  - the email composer's scheduling backstop (`shopper-email` `calculateScheduledTime`),
 *  - the decision prompt's description of the window,
 *  - the save-time validation (`EngagementRulesSchema`, `SmsConfigSchema`, `PUT /workspaces`,
 *    and both settings cards in the web app),
 *  - the soak's `quiet-hours-send` invariant.
 *
 * Before this there were four windows (22→08 in two fixtures and a dead discount-governance
 * field, 21→08 everywhere else, plus whatever the merchant set) and four predicates. A window
 * that is defined twice is a window that disagrees with itself.
 *
 * ── Which window a channel obeys ──────────────────────────────────────────────────────────
 *
 *  - email → `engagement.quietHours.overrides.email`, else `engagement.quietHours.global`, else
 *    the default. No floor: email has no calling-window regulation.
 *  - sms   → `workspace.smsConfig.quietHours{Start,End}` (the SMS limits card), else the default,
 *    UNIONED with the TCPA floor (`SMS_QUIET_HOURS_FLOOR`). Quiet if either window says so, so a
 *    merchant can quiet more than the floor and never less.
 *
 * ── What `start === end` means ────────────────────────────────────────────────────────────
 *
 * Always quiet (the wrapping branch matches every hour). MAR-154 preserved that meaning so no
 * workspace sitting on it would silently go LOUD, and this module keeps it. What changes is that
 * nothing can ARRIVE at it any more: `quietWindowProblem` is what every save path refuses with.
 */

/** The platform default window, and the window a new store starts on: 21:00–08:00 local. */
const DEFAULT_QUIET_HOURS = Object.freeze({ start: 21, end: 8 });

/**
 * The window no text message may be sent in, whatever the merchant sets — the US telemarketing
 * calling window (TCPA, 9pm–8am recipient-local). Unioned, never clamped: see
 * `isInQuietHoursWithFloor` in `./sms-compliance/quiet-hours`.
 */
const SMS_QUIET_HOURS_FLOOR = DEFAULT_QUIET_HOURS;

/** The channels quiet hours govern. On-site chat is not outbound and has none. */
const QUIET_HOURS_CHANNELS = Object.freeze(['email', 'sms']);

const HOURS_IN_DAY = 24;

/**
 * An IMMEDIATE reply is exempt from quiet hours — the merchant's window AND the 9pm–8am floor
 * (MAR-159 review, decided; pending counsel — `docs/decisions/decisions-open.md`).
 *
 * The shopper texted first, minutes ago: answering is the second half of a conversation they
 * started, not a solicitation, and holding it overnight (or dropping it) is the worse answer.
 * "Immediate" is bounded by the shopper's own inbound: a reply produced within this many
 * milliseconds of the text it answers. Anything later — a redrive, a stale retry, a reply to an
 * inbound whose time is unknown — is not immediate and obeys quiet hours like any text.
 */
const IMMEDIATE_REPLY_WINDOW_MS = 15 * 60 * 1000;

/**
 * Is a reply sent at `now` an immediate answer to an inbound received at `inboundAt`?
 * An absent, unparseable or future inbound time is NOT immediate — the exemption must be proven.
 *
 * @param {string|number|Date|null|undefined} inboundAt
 * @param {Date} [now]
 * @returns {boolean}
 */
function isImmediateReply(inboundAt, now = new Date()) {
  if (inboundAt === null || inboundAt === undefined || inboundAt === '') return false;
  const at = new Date(inboundAt).getTime();
  if (!Number.isFinite(at)) return false;
  const age = now.getTime() - at;
  return age >= -60 * 1000 && age <= IMMEDIATE_REPLY_WINDOW_MS; // a minute of clock skew either way
}

/**
 * THE predicate: is local `hour` (0–23) inside `window`?
 *
 *  - start > end  — spans midnight (21→08): quiet if hour >= start OR hour < end.
 *  - start < end  — inside one day (02→06): quiet if hour >= start AND hour < end.
 *  - start === end — the wrapping branch, i.e. every hour (see the module docblock).
 *
 * @param {{start:number,end:number}} window
 * @param {number} hour
 * @returns {boolean}
 */
function isQuietHour(window, hour) {
  const { start, end } = window;
  return start < end
    ? (hour >= start && hour < end)
    : (hour >= start || hour < end);
}

/** The windows a channel's quiet hours are the union of: its own, plus the floor for SMS. */
function windowsFor(window, floor) {
  return floor ? [window, floor] : [window];
}

/**
 * Does the union of these windows cover every hour of the day — i.e. would nothing ever send?
 * Exact for whole-hour windows, which is all the schema admits.
 *
 * @param {{start:number,end:number}} window
 * @param {{ floor?: {start:number,end:number}|null }} [opts]
 * @returns {boolean}
 */
function quietWindowNeverOpens(window, { floor = null } = {}) {
  const windows = windowsFor(window, floor);
  for (let hour = 0; hour < HOURS_IN_DAY; hour++) {
    if (!windows.some((w) => isQuietHour(w, hour))) return false;
  }
  return true;
}

/**
 * Why this window cannot be saved, or `null` when it can.
 *
 * A window that never opens means "never send on this channel again". That is not a quiet-hours
 * setting, it is switching the channel off, and the place to do that is the channel's own switch
 * — so it is refused rather than stored, and the merchant is told what it would have meant.
 *
 * @param {{start:number,end:number}} window
 * @param {{ floor?: {start:number,end:number}|null }} [opts]
 * @returns {null | { code: 'same_start_and_end'|'covers_whole_day_with_floor', message: string }}
 */
function quietWindowProblem(window, { floor = null } = {}) {
  if (!window || !Number.isInteger(window.start) || !Number.isInteger(window.end)) return null;
  if (window.start === window.end) {
    return {
      code: 'same_start_and_end',
      message: 'Quiet hours that start and end at the same hour cover the whole day, so nothing would ever be sent. Pick two different hours.'
    };
  }
  if (quietWindowNeverOpens(window, { floor })) {
    return {
      code: 'covers_whole_day_with_floor',
      message: 'Together with the 9pm–8am window that always applies to text messages, these quiet hours cover the whole day, so no text would ever be sent. To stop text messages, switch SMS off instead.'
    };
  }
  return null;
}

/**
 * The window `channel` obeys, read from the config the merchant actually edits for it.
 *
 * `??` throughout, never `||`: 0 (midnight) is a real hour.
 *
 * @param {string} channel
 * @param {{ rules?: object, workspace?: object }} [config]
 * @returns {{ window: {start:number,end:number}, floor: {start:number,end:number}|null, source: string }}
 */
function resolveQuietWindow(channel, { rules, workspace } = {}) {
  if (channel === 'sms') {
    const sms = workspace?.smsConfig;
    const configured = Number.isInteger(sms?.quietHoursStart) || Number.isInteger(sms?.quietHoursEnd);
    return {
      window: {
        start: sms?.quietHoursStart ?? DEFAULT_QUIET_HOURS.start,
        end: sms?.quietHoursEnd ?? DEFAULT_QUIET_HOURS.end
      },
      floor: SMS_QUIET_HOURS_FLOOR,
      source: configured ? 'smsConfig' : 'default'
    };
  }
  const override = rules?.quietHours?.overrides?.[channel];
  const global = rules?.quietHours?.global;
  const base = override || global || DEFAULT_QUIET_HOURS;
  return {
    window: { start: base.start ?? DEFAULT_QUIET_HOURS.start, end: base.end ?? DEFAULT_QUIET_HOURS.end },
    floor: null,
    source: override ? `engagement.overrides.${channel}` : (global ? 'engagement.global' : 'default')
  };
}

const pad = (h) => `${String(h).padStart(2, '0')}:00`;

/**
 * One line naming the window `channel` obeys — for prompts and logs, so what the model is told
 * is what the enforcer applies. Rendered from `resolveQuietWindow`, never from a second read.
 */
function describeQuietWindow(channel, config = {}) {
  const { window, floor } = resolveQuietWindow(channel, config);
  const own = `${pad(window.start)}–${pad(window.end)}`;
  if (!floor) return own;
  const sameAsFloor = window.start === floor.start && window.end === floor.end;
  return sameAsFloor ? own : `${own}, and never ${pad(floor.start)}–${pad(floor.end)}`;
}

module.exports = {
  DEFAULT_QUIET_HOURS,
  SMS_QUIET_HOURS_FLOOR,
  QUIET_HOURS_CHANNELS,
  IMMEDIATE_REPLY_WINDOW_MS,
  isImmediateReply,
  isQuietHour,
  quietWindowNeverOpens,
  quietWindowProblem,
  resolveQuietWindow,
  describeQuietWindow
};
